import { useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { boatIdAtom } from 'components/checkout/v2/jotaiStore';
import { useBoatDetails, useGetBoatAvailableInsurancesQuery } from 'components/pdp/v3/hooks';
import { renterInsurerCompanyAtom } from 'components/checkout/buoy/jotaiStore';
import { getAsString, constructScreeningQuestionsQueryString } from 'helpers';
import { useRouter } from 'next/router';
import { useBookingTrip } from 'components/booking/queryHooks';
import { getParamsForScreeningQuestions } from 'components/checkout/add-ons/helpers';

// this should be refactored to accept the insurer or renamed to be buoy specific
export function useIsRenterInsuranceEnabled(bookingId?: string) {
  const [isBuoyLoading, setIsBuoyLoading] = useState(true);
  const [isApprovedForBuoy, setIsApprovedForBuoy] = useState(false);
  const router = useRouter();

  const [insurerCompany, setRenterInsuranceCompany] = useAtom(renterInsurerCompanyAtom);
  const { bookingTrip } = useBookingTrip({ enabled: !!bookingId }, bookingId);

  const packageId = !bookingId ? getAsString(router.query['booking[package_public_id]']) : bookingTrip?.package?.id;
  const boatId = useAtomValue(boatIdAtom);

  const {
    boatDetails: { packages: boatPackages },
    isLoading: isLoadingBoatDetails,
    isSuccess: isSuccessBoatDetails,
  } = useBoatDetails(boatId);

  const {
    boatAvailableInsurances,
    isSuccess,
    isLoading: isLoadingInsurances,
  } = useGetBoatAvailableInsurancesQuery(boatId);

  useEffect(() => {
    if (!isSuccess) return;
    if (!boatPackages) return;
    if (!boatAvailableInsurances) return;
    const boatPackage = boatPackages.find((p) => p.id === packageId);

    const canBeBuoy = boatPackage?.eligible_insurers?.includes('buoy');

    if (canBeBuoy) setRenterInsuranceCompany('buoy');

    setIsApprovedForBuoy(
      (boatAvailableInsurances.providers || []).some(
        (p) => p.name?.includes(insurerCompany || 'buoy') && p.status === 'approved' && canBeBuoy
      )
    );
    setIsBuoyLoading(isLoadingInsurances || isLoadingBoatDetails);
  }, [isSuccess, boatAvailableInsurances, insurerCompany, isLoadingInsurances, boatPackages]);

  return { isBuoyEnabled: isApprovedForBuoy, isBuoyLoading, isSuccess: isSuccess && isSuccessBoatDetails };
}

export const getValidInsurerFromString = (insurer: string, allowedInsurers: ('buoy' | 'boat_us')[] = []) => {
  let finalInsurer: 'buoy' | 'boat_us' = null;
  if (insurer === 'buoy') finalInsurer = 'buoy';
  if (insurer === 'boat_us') finalInsurer = 'boat_us';
  return allowedInsurers.includes(finalInsurer) ? finalInsurer : null;
};

export const getNeedsBuoyScreening = ({ renterInsurance, wantsInsurance, isBuoyBooking }) => {
  // if they don't want insurance or are already approved
  if (wantsInsurance !== 'yes' || renterInsurance?.renter_insurance_status === 'approved') {
    return false;
  }

  // if it's buoy and we aren't already approved (above) we need screening
  if (isBuoyBooking) {
    return true;
  }

  return false;
};

export const redirectToScreeningIfNeeded = ({ queryParams, boatId, hasBookingId, bookingInfo, insuranceStatus }) => {
  if (insuranceStatus?.renter_insurance_status === 'approved' || insuranceStatus?.renter_insurance_status === 'denied')
    return;

  let questionsParams;

  if (hasBookingId) {
    questionsParams = {
      booking_id: bookingInfo?.id,
    };
  } else {
    questionsParams = getParamsForScreeningQuestions({
      ...queryParams,
      boat_public_id: boatId,
    });
  }

  const questionsURLStringParams = constructScreeningQuestionsQueryString(questionsParams);

  window.location.href = `/insurers/buoy/renter_application/screening_questions?${questionsURLStringParams}&redirect_to=${encodeURIComponent(
    window.location.pathname + window.location.search
  )}`;
};

export const getIsBuoyBooking = ({ bookingTrip, thisPackage }) => {
  const isInquiry = Object.keys(bookingTrip).length > 0;
  const { insurer, package: bookingPackage } = bookingTrip;
  let isBuoyEnabled;

  // if there is already a booking
  if (isInquiry) {
    // if the renter has already selected buoy
    if (insurer) isBuoyEnabled = insurer === 'buoy';
    else isBuoyEnabled = bookingPackage?.eligible_insurers?.includes('buoy');
  } else {
    isBuoyEnabled = thisPackage?.eligible_insurers?.includes('buoy');
  }

  return isBuoyEnabled;
};
